/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import {
	Sheet,
	SheetContent,
	SheetTitle,
	SheetDescription
} from "src/components/ui/sheet";
import { REACTIONS } from "src/features/SocialFeedContent/components/ReactionPicker/ReactionPicker";
import {
	usePostReactions,
	usePostReactionSummary
} from "src/features/SocialFeedContent/hooks";
import { useAvatar } from "src/hooks";

interface ReactionsSheetProps {
	postId: string | null;
	isOpen: boolean;
	onClose: () => void;
}

type TabKey = "all" | string;

const reactionMeta = (value: string) =>
	REACTIONS.find((r) => r.value === value);

const ReactorRow: FC<{
	userId: string | null;
	name: string;
	reaction: string;
}> = ({ userId, name, reaction }) => {
	const { data: imageUri } = useAvatar({
		userId: userId ?? undefined,
		enabled: Boolean(userId)
	});
	const initials = name
		? name
				.split(" ")
				.slice(0, 2)
				.map((n) => n[0])
				.join("")
				.toUpperCase()
		: "?";
	const emoji = reactionMeta(reaction)?.emoji ?? "👍";

	return (
		<div className="flex items-center gap-3 px-4 py-2.5">
			<div className="relative w-10 h-10 shrink-0">
				{imageUri ? (
					<img
						src={imageUri}
						alt=""
						aria-hidden="true"
						className="w-10 h-10 rounded-full object-cover"
					/>
				) : (
					<div
						aria-hidden="true"
						className="w-10 h-10 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center text-xs font-medium"
					>
						{initials}
					</div>
				)}
				<span
					aria-hidden="true"
					className="absolute -bottom-0.5 -right-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-[12px] ring-2 ring-white shadow-sm"
				>
					{emoji}
				</span>
			</div>
			<span className="text-sm font-medium text-foreground truncate">
				{name || "Unknown user"}
			</span>
		</div>
	);
};

const ReactionsSheet: FC<ReactionsSheetProps> = ({
	postId,
	isOpen,
	onClose
}) => {
	const [tab, setTab] = useState<TabKey>("all");
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (isOpen) setTab("all");
	}, [isOpen, postId]);

	const summary = usePostReactionSummary(postId ?? "", isOpen);

	const reactionFilter = tab === "all" ? null : tab;
	const list = usePostReactions(postId ?? "", reactionFilter, isOpen);

	const {
		data: listData,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		isLoading
	} = list ?? {};

	const fetchNextPageRef = useRef(fetchNextPage);
	fetchNextPageRef.current = fetchNextPage;

	useEffect(() => {
		const node = sentinelRef.current;
		if (!node || !isOpen) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPageRef.current?.();
				}
			},
			{ rootMargin: "100px" }
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [isOpen, hasNextPage, isFetchingNextPage]);

	const rows = listData?.pages.flatMap((p) => p.results) ?? [];

	const visibleTypes = Object.entries(summary?.data?.byType ?? {})
		.filter(([, n]) => n > 0)
		.sort((a, b) => b[1] - a[1]);

	const total = summary?.data?.total ?? 0;

	return (
		<Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<SheetContent
				side="right"
				className="w-full sm:max-w-md p-0 flex flex-col"
			>
				<div className="px-4 pt-4 pb-3 border-b border-border">
					<SheetTitle>Reactions</SheetTitle>
					<SheetDescription className="sr-only">
						People who reacted to this post
					</SheetDescription>
				</div>

				<div
					role="group"
					aria-label="Filter reactions by type"
					aria-controls="reactions-list"
					className="flex gap-1 overflow-x-auto border-b border-border px-2"
				>
					<button
						type="button"
						aria-pressed={tab === "all"}
						onClick={() => setTab("all")}
						className={[
							"px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors",
							tab === "all"
								? "border-foreground text-foreground font-semibold"
								: "border-transparent text-muted-foreground hover:text-foreground"
						].join(" ")}
					>
						All {total}
					</button>
					{visibleTypes.map(([value, count]) => {
						const meta = reactionMeta(value);
						return (
							<button
								key={value}
								type="button"
								aria-pressed={tab === value}
								onClick={() => setTab(value)}
								className={[
									"px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5",
									tab === value
										? "border-foreground text-foreground font-semibold"
										: "border-transparent text-muted-foreground hover:text-foreground"
								].join(" ")}
							>
								<span aria-hidden="true">{meta?.emoji ?? value}</span>
								<span className="sr-only">{meta?.name ?? value}</span>
								<span>{count}</span>
							</button>
						);
					})}
				</div>

				<div
					id="reactions-list"
					aria-live="polite"
					className="flex-1 overflow-y-auto"
				>
					{isLoading && (
						<div className="flex justify-center py-8">
							<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
						</div>
					)}
					{!isLoading && rows.length === 0 && (
						<p className="text-sm text-muted-foreground text-center py-8">
							No reactions yet.
						</p>
					)}
					{rows.map((row) => (
						<ReactorRow
							key={row.id}
							userId={row.user}
							name={row.name}
							reaction={row.reaction}
						/>
					))}
					{hasNextPage && (
						<div ref={sentinelRef} className="py-3 flex justify-center">
							{isFetchingNextPage && (
								<div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin" />
							)}
						</div>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
};

export default ReactionsSheet;
